import { useEffect, useMemo, useReducer } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as P from '@blisspointmedia/bpm-types/dist/Performance';
import {
  B2BCreativeTags,
  CreativeInsightsIsciData
} from '@blisspointmedia/bpm-types/dist/Creative';
import { CreativeMapItem } from '@src/redux/creative';
import {
  awaitJSON,
  COMET_TRIGGER_FIELD,
  CreativeLambdaFetch,
  LinearPerformanceLambdaFetch
} from '@src/utils/fetch-utils';
import { useSetError } from '@src/redux/modals';
import {
  ConceptMap,
  ConceptOptions,
  EMPTY_SELECTION,
  EnabledKpis,
  ImportanceData,
  ImportanceResponse,
  SelectionActionType,
  TEST_COMPANY,
  TEST_COMPANY_PROXY
} from './creative-insights-constants';
import {
  selectionReducer,
  toConceptMap,
  toEnabledKpis,
  toImportanceData,
  toSuppressedAttributes
} from './creative-insights-utils';

const loadDemoData = () => import('./creative-insights-demo-data');

export const toCompanyParam = (company: string): string =>
  company === TEST_COMPANY ? TEST_COMPANY_PROXY : company;

export const useEnabledKpis = (
  company: string,
  isInternal: boolean
): ReturnType<typeof useQuery<EnabledKpis>> => {
  const companyParam = toCompanyParam(company);
  return useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['creativeInsights', 'enabledKpis', companyParam, isInternal],
    enabled: Boolean(companyParam),
    queryFn: async (): Promise<EnabledKpis> => {
      const rows = await awaitJSON<{ cid: string; enabled: boolean }[]>(
        await CreativeLambdaFetch('/getEnabledKpis', { params: { company: companyParam } })
      );
      return toEnabledKpis(rows, isInternal);
    }
  });
};

// Only the KPIs enabled for creative insights are offered in the picker.
export const useKpiPickerMap = (
  company: string,
  viewableKpis: string[] | undefined
): ReturnType<typeof useQuery<P.GetKpiPickerMapResponse>> => {
  const companyParam = toCompanyParam(company);
  return useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['creativeInsights', 'kpiPickerMap', companyParam],
    enabled: Boolean(companyParam && viewableKpis),
    queryFn: async (): Promise<P.GetKpiPickerMapResponse> =>
      awaitJSON<P.GetKpiPickerMapResponse>(
        await LinearPerformanceLambdaFetch<P.GetKpiMetaDataParams>('/getKpiPickerMap', {
          params: { company: companyParam }
        })
      ),
    select: kpiMap =>
      Object.fromEntries(Object.entries(kpiMap).filter(([key]) => viewableKpis?.includes(key)))
  });
};

export const useInitialKpi = (company: string): ReturnType<typeof useQuery<string>> => {
  const companyParam = toCompanyParam(company);
  return useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['creativeInsights', 'initialKpi', companyParam],
    enabled: Boolean(companyParam),
    queryFn: async (): Promise<string> =>
      awaitJSON<string>(
        await CreativeLambdaFetch('/getInitialKpi', { params: { company: companyParam } })
      )
  });
};

export const useIsciData = (
  company: string,
  kpi: string
): ReturnType<typeof useQuery<CreativeInsightsIsciData>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['creativeInsights', 'isciData', company, kpi],
    enabled: Boolean(kpi),
    queryFn: async (): Promise<CreativeInsightsIsciData> => {
      if (company === TEST_COMPANY) {
        return (await loadDemoData()).TEST_ISCI_DATA;
      }
      return awaitJSON<CreativeInsightsIsciData>(
        await CreativeLambdaFetch('/getCreativeInsightsIsciData', {
          params: { kpi, [COMET_TRIGGER_FIELD]: true }
        })
      );
    }
  });

export const useImportanceData = (
  company: string,
  kpi: string
): ReturnType<typeof useQuery<ImportanceData>> => {
  const companyParam = toCompanyParam(company);
  return useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['creativeInsights', 'importanceData', company, kpi],
    enabled: Boolean(companyParam && kpi),
    // The demo company keeps the real last-modified date.
    queryFn: async (): Promise<ImportanceData> => {
      const fetched = await awaitJSON<ImportanceResponse>(
        await CreativeLambdaFetch('/getCreativeInsightsImportanceData', {
          params: { company: companyParam, kpi }
        })
      );
      if (company === TEST_COMPANY) {
        const { TEST_IMPORTANCE_DATA } = await loadDemoData();
        return toImportanceData({ ...TEST_IMPORTANCE_DATA, lastModified: fetched.lastModified });
      }
      return toImportanceData(fetched);
    }
  });
};

// Users can't pick which attributes to suppress yet, so this is the default set.
export const useSuppressedAttributes = (
  company: string
): ReturnType<typeof useQuery<Set<string>>> => {
  const companyParam = toCompanyParam(company);
  return useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['creativeInsights', 'suppressedAttributes', companyParam],
    enabled: Boolean(companyParam),
    queryFn: async (): Promise<Set<string>> => {
      const attributes = await awaitJSON<string | null>(
        await CreativeLambdaFetch('/getSuppressedAttributes', { params: { company: companyParam } })
      );
      return toSuppressedAttributes(attributes);
    }
  });
};

export const useConceptMap = (company: string): ReturnType<typeof useQuery<ConceptMap>> => {
  const companyParam = toCompanyParam(company);
  return useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['creativeInsights', 'conceptMap', company],
    enabled: Boolean(companyParam),
    queryFn: async (): Promise<ConceptMap> => {
      if (company === TEST_COMPANY) {
        return (await loadDemoData()).TEST_CONCEPT_MAP;
      }
      return toConceptMap(
        await awaitJSON<ConceptOptions>(
          await CreativeLambdaFetch('/get_creative_options', { params: { cid: companyParam } })
        )
      );
    }
  });
};

export const useB2BCreativeTags = (
  company: string
): ReturnType<typeof useQuery<B2BCreativeTags>> => {
  const companyParam = toCompanyParam(company);
  return useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['creativeInsights', 'b2bCreativeTags', companyParam],
    enabled: Boolean(companyParam),
    queryFn: async (): Promise<B2BCreativeTags> =>
      awaitJSON<B2BCreativeTags>(
        await CreativeLambdaFetch('/getB2BCreativeTags', { params: { company: companyParam } })
      )
  });
};

export const useDemoCreativeList = (
  company: string
): ReturnType<typeof useQuery<CreativeMapItem[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['creativeInsights', 'demoCreativeList'],
    enabled: company === TEST_COMPANY,
    staleTime: Infinity,
    queryFn: async (): Promise<CreativeMapItem[]> => (await loadDemoData()).TEST_CREATIVE_LIST
  });

// React Query has no catch to hook into, so failures are surfaced from an effect keyed on the
// first failed query, which shows one error modal at a time.
export const useSurfaceQueryErrors = (
  queryErrors: { error: Error | null; prefix?: string }[]
): void => {
  const setError = useSetError();
  const firstFailure = queryErrors.find(({ error }) => error);
  const error = firstFailure?.error;
  const prefix = firstFailure?.prefix;
  useEffect(() => {
    if (error) {
      setError({
        message: prefix ? `${prefix} Error: ${error.message}.` : error.message,
        reportError: error
      });
    }
  }, [error, prefix, setError]);
};

export const useSelection = () => {
  const [selection, dispatch] = useReducer(selectionReducer, EMPTY_SELECTION);
  const actions = useMemo(
    () => ({
      toggleAttribute: (attribute: string) =>
        dispatch({ type: SelectionActionType.TOGGLE_ATTRIBUTE, attribute }),
      selectAttribute: (attribute: string) =>
        dispatch({ type: SelectionActionType.SELECT_ATTRIBUTE, attribute }),
      clearAttribute: () => dispatch({ type: SelectionActionType.CLEAR_ATTRIBUTE }),
      toggleTag: (tag: string) => dispatch({ type: SelectionActionType.TOGGLE_TAG, tag }),
      selectTag: (tag: string) => dispatch({ type: SelectionActionType.SELECT_TAG, tag }),
      clearTag: () => dispatch({ type: SelectionActionType.CLEAR_TAG }),
      selectCreative: (creativeKey: string) =>
        dispatch({ type: SelectionActionType.SELECT_CREATIVE, creativeKey })
    }),
    []
  );
  return { selection, actions };
};

export type SelectionActions = ReturnType<typeof useSelection>['actions'];
